import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { sendActionChatStream, confirmAction } from './services/api';
import { extractPageContext, extractPartitionId } from './utils/pageContext';

const ActionChat = ({ config }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [deepSearch, setDeepSearch] = useState(false);
  const [streamStatus, setStreamStatus] = useState([]);  // array of {type, text} for status/thinking log
  const messagesEndRef = useRef(null);
  const streamLogRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-scroll the stream status log and the messages container when new status arrives
  useEffect(() => {
    if (streamLogRef.current) {
      streamLogRef.current.scrollTop = streamLogRef.current.scrollHeight;
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [streamStatus]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const buildConversationHistory = (currentMessages) => {
    const msgs = currentMessages || messages;
    return msgs
      .filter(msg => !msg.confirmRequired) // skip pending confirm cards
      .map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
  };

  const getPageContext = () => {
    const pageUrl = window.location.href;
    const ctx = extractPageContext(pageUrl);
    return ctx ? JSON.stringify(ctx) : null;
  };

  const handleResponse = (result, prevMessages) => {
    if (result.status === 'CONFIRM_REQUIRED') {
      setPendingConfirm({
        pendingActionId: result.pendingActionId,
        pendingAction: result.pendingAction,
      });
      return [...prevMessages, {
        role: 'assistant',
        content: result.message,
        confirmRequired: true,
        pendingAction: result.pendingAction,
      }];
    } else if (result.status === 'SUCCESS') {
      return [...prevMessages, {
        role: 'assistant',
        content: result.message || 'Done.',
      }];
    } else {
      return [...prevMessages, {
        role: 'assistant',
        content: result.message || 'An error occurred.',
        isError: true,
      }];
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    const messageText = deepSearch ? '[DEEP SEARCH] ' + text : text;
    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const payload = {
        message: messageText,
        pageUrl: window.location.href,
        pageContext: getPageContext(),
        partitionId: extractPartitionId(),
        conversationHistory: buildConversationHistory(newMessages),
      };

      setStreamStatus([]);

      const result = await sendActionChatStream(
        config.apiBaseUrl,
        payload,
        (statusText) => {
          setStreamStatus(prev => [...prev, { type: 'status', text: statusText }]);
        },
        (thinkingText) => {
          setStreamStatus(prev => [...prev, { type: 'thinking', text: thinkingText }]);
        },
      );
      setStreamStatus([]);
      setMessages(prev => handleResponse(result, prev));
    } catch (err) {
      console.error('Action chat error:', err);
      setStreamStatus([]);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: err.message || 'Failed to communicate with the server.',
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!pendingConfirm || loading) return;
    setLoading(true);

    try {
      // Clear the confirm card on the last message
      setMessages(prev => {
        const updated = [...prev];
        const lastIdx = updated.length - 1;
        if (lastIdx >= 0 && updated[lastIdx].confirmRequired) {
          updated[lastIdx] = { ...updated[lastIdx], confirmRequired: false };
        }
        return updated;
      });

      const confirmPayload = {
        pendingActionId: pendingConfirm.pendingActionId,
        conversationHistory: buildConversationHistory(),
        partitionId: extractPartitionId(),
        pageContext: getPageContext(),
      };

      const result = await confirmAction(config.apiBaseUrl, confirmPayload);
      setPendingConfirm(null);

      // handleResponse will set a new pendingConfirm if the chain continues
      setMessages(prev => handleResponse(result, prev));
    } catch (err) {
      console.error('Confirm error:', err);
      setPendingConfirm(null);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Failed to confirm action: ' + (err.message || 'Unknown error'),
        isError: true,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPendingConfirm(null);
    setMessages(prev => {
      const updated = [...prev];
      const lastIdx = updated.length - 1;
      if (lastIdx >= 0 && updated[lastIdx].confirmRequired) {
        updated[lastIdx] = { ...updated[lastIdx], confirmRequired: false };
      }
      updated.push({
        role: 'assistant',
        content: 'Action cancelled.',
      });
      return updated;
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="action-chat-container">
      <div className="action-chat-messages">
        {messages.length === 0 && (
          <p className="action-chat-placeholder">
            Ask me to do something in Active Steward, e.g. "List all constituents" or "Create a study called Benzene Toxicity"
          </p>
        )}
        {messages.map((msg, idx) => {
          // Split workings from main content
          const workingsDelimiter = '<!--WORKINGS-->';
          const hasWorkings = msg.role === 'assistant' && msg.content && msg.content.includes(workingsDelimiter);
          let mainContent = hasWorkings ? msg.content.split(workingsDelimiter)[0].trim() : msg.content;
          const workingsContent = hasWorkings ? msg.content.split(workingsDelimiter).slice(1).join(workingsDelimiter).trim() : null;

          // Replace CSV download markers with actual links using the API base URL
          const csvPattern = /\*\*CSV_DOWNLOAD:([^:]+):([^:]+):(\d+)\*\*/g;
          const apiBase = config.apiBaseUrl.replace(/\/api\/v1\/?$/, '');
          mainContent = mainContent.replace(csvPattern, (_, csvId, fileName, count) =>
            `**[Download full results CSV](${apiBase}/download/literature-csv/${csvId})** — ${count} papers/assays with filtering decisions (${fileName})`
          );

          const markdownComponents = {
            a: ({ node, children, ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>
            ),
          };

          return (
          <div key={idx} className={`action-chat-msg action-chat-msg-${msg.role} ${msg.isError ? 'action-chat-msg-error' : ''}`}>
            {msg.role === 'assistant' ? (
              <div className="action-chat-msg-content">
                <ReactMarkdown
                  remarkPlugins={[remarkBreaks]}
                  components={markdownComponents}
                >{mainContent}</ReactMarkdown>
                {workingsContent && (
                  <details className="action-chat-workings">
                    <summary className="action-chat-workings-summary">Show workings</summary>
                    <div className="action-chat-workings-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkBreaks]}
                        components={markdownComponents}
                      >{workingsContent}</ReactMarkdown>
                    </div>
                  </details>
                )}
                {msg.confirmRequired && pendingConfirm && (
                  <div className="action-chat-confirm-card">
                    <div className="action-chat-confirm-buttons">
                      <button
                        className="action-chat-btn-confirm"
                        onClick={handleConfirm}
                        disabled={loading}
                      >
                        Confirm
                      </button>
                      <button
                        className="action-chat-btn-cancel"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="action-chat-msg-content">{msg.content}</div>
            )}
          </div>
          );
        })}
        {loading && (
          <div className="action-chat-msg action-chat-msg-assistant">
            <div className="action-chat-msg-content">
              {streamStatus.length > 0 ? (
                <div className="action-chat-stream-log" ref={streamLogRef}>
                  {streamStatus.map((entry, idx) => (
                    <div key={idx} className={`action-chat-stream-entry action-chat-stream-${entry.type}`}>
                      {entry.type === 'status' && <span className="action-chat-stream-icon">⚙</span>}
                      {entry.type === 'thinking' && <span className="action-chat-stream-icon">💭</span>}
                      <span>{entry.text}</span>
                    </div>
                  ))}
                  <div className="action-chat-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              ) : (
                <div className="action-chat-typing">
                  <span></span><span></span><span></span>
                </div>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="action-chat-input-area">
        <label className="action-chat-deep-search" title="Deep search: fetch and classify every result (slower but comprehensive)">
          <input
            type="checkbox"
            checked={deepSearch}
            onChange={e => setDeepSearch(e.target.checked)}
            disabled={loading}
          />
          <span>Deep search</span>
        </label>
        <textarea
          ref={inputRef}
          className="action-chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          disabled={loading}
        />
        <button
          className="action-chat-send"
          onClick={handleSend}
          disabled={loading || !input.trim()}
          aria-label="Send message"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '18px', height: '18px' }}>
            <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ActionChat;
